import styles from './simple-table.module.scss';
import { Pagination } from '../pagination/pagination';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, GridReadyEvent, GridSizeChangedEvent, PaginationChangedEvent, RowClassRules } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalize, InputAdornment } from '@mui/material';
import { CheckboxLabel, DateRangePicker, GRID_BREAK_POINT, Icon, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography, useWindowSizeHook } from '@frontend-workspace/react-shared-lib';
import { AG_GRID_LOCALE_NBNO } from './ag-grid.locale.nb';
import { AG_GRID_LOCALE_EN } from './ag-grid.locale.en';
import TableFilters, { iTableSwitch } from '../table-filters/table-filters';
import { differenceInDays, differenceInYears } from 'date-fns';
import { addMonths, addYears, differenceInMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import SimpleTableSkeleton from './simple-table-skeleton';

/* eslint-disable-next-line */
export interface SimpleTableProps {
  data: Array<any>,
  headers: Array<any>,
  headerHeight?: number,
  pageSize?: number,
  disableSearch?: boolean,
  disablePagination?: boolean,
  allSelected?: boolean,
  radioFilterOpts?: RadioFilterOpts,
  onGridReady?: (api: GridApi) => void,
  onSelectionChanged?: (...args: any[]) => void,
  onCheckboxChanged?: (...args: any[]) => void,
  onDateChanged?: (...args: any[]) => void,
  onSelectAll?: (...args: any[]) => void,
  isCompactTable?: boolean,
  resizable?: boolean,
  dateFilter?: boolean,
  onSetDateRange?: (...args: any[]) => void,
  dateRange?: any,
  fitHeight?: boolean,
  hasColumnFilters?: boolean,
  tableFilters?: iTableSwitch[],
  singleCellSelection?: boolean,
  rowClassRules?: RowClassRules,
  isLoading?: boolean,
  getRowStyle?: (params: any) => any,
  onSaveColumnConfig?: (columns: ColumnConfig[] | null) => void,
  tableConfig?: TableConfig,
  customBreakPoint?: number
}

export enum DateSelectionEnum {
  "All" = 0,
  "LastDay" = 1,
  "LastWeek" = 2,
  "LastMonth" = 3,
  "LastYear" = 4,
  "Custom" = 5,
  "Today" = 6,
  "NextWeek" = 7,
  "NextMonth" = 8,
  "NextYear" = 9
}

export interface ColumnConfig {
  columnName: string,
  isHidden: boolean,
  sortDirection: string
}

export interface TableConfig {
  columns: ColumnConfig[] | undefined,
  onSaveTableConfig: (config: ColumnConfig[] | null, filterModel: any) => void,
  filterModel: any
}

export interface RadioFilterOpts {
  onChange: (...args: any[]) => void,
  values: string[],
  value: string
}

export function SimpleTable(props: SimpleTableProps) {
  if (props.headers.length === 0) return null;
  const { t, i18n } = useTranslation();
  const lineHeight = 50;
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);
  const [currentPage, setCurrentPage] = useState<number>();
  const [pageCount, setPageCount] = useState<number>();
  const [pageSize, setPageSize] = useState<number>(props.pageSize || 10);
  const [viewWidth, viewHeight] = useWindowSizeHook();
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const [breakPoint, setBreakPoint] = useState<number>(props.customBreakPoint || GRID_BREAK_POINT);
  // const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  useEffect(() => {
    if (gridApi) {
      gridApi.setFilterModel(props.tableConfig?.filterModel);
    }
  }, [props.tableConfig]);

  // const gridStyle = useMemo(() => {
  //   if (props.fitHeight) {
  //     const height = props.data.length * lineHeight + 48;
  //     return ({ height: height, width: '100%' })
  //   }
  //   return ({ height: '100%', width: '100%' })
  // }, [props.data]);

  const getDateSelection = () => {
    if (!props.dateRange) return DateSelectionEnum.LastDay;
    const startDate = props.dateRange[0];
    const endDate = props.dateRange[1];
    if (differenceInDays(endDate, startDate) === 1) return DateSelectionEnum.LastDay;
    if (differenceInDays(endDate, startDate) === 7) return DateSelectionEnum.LastWeek;
    if (addMonths(startDate, 1).getDate() === endDate.getDate() && differenceInMonths(endDate, startDate) === 1 && endDate.getMonth() === new Date().getMonth()) return DateSelectionEnum.LastMonth;
    if (addYears(startDate, 1).getDate() === endDate.getDate() && differenceInYears(endDate, startDate) === 1) return DateSelectionEnum.LastYear;
    return DateSelectionEnum.Custom;
  };

  const [dateSelection, setDateSelection] = useState(getDateSelection());

  const dateSelections = [
    //{ id: DateSelectionEnum.All, name: 'Alle' },
    { id: DateSelectionEnum.LastDay, name: t("simpleTable.last24Hours") },
    { id: DateSelectionEnum.LastWeek, name: t("simpleTable.lastWeek") },
    { id: DateSelectionEnum.LastMonth, name: t("simpleTable.lastMonth") },
    { id: DateSelectionEnum.LastYear, name: t("simpleTable.lastYear") },
    { id: DateSelectionEnum.Custom, name: t("simpleTable.custom") },
  ];

  const handleDateSelection = (event: any) => {
    if (!props.onSetDateRange) { return; }
    const start = new Date();
    switch (event.target.value) {
      case DateSelectionEnum.All:
        props.onSetDateRange(null);
        break;
      case DateSelectionEnum.LastDay:
        start.setDate(start.getDate() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastWeek:
        start.setDate(start.getDate() - 7);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastMonth:
        start.setMonth(start.getMonth() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.LastYear:
        start.setFullYear(start.getFullYear() - 1);
        props.onSetDateRange([start, new Date()]);
        break;
      case DateSelectionEnum.Custom:
        props.onSetDateRange(null);
        break;
      default:
        break;
    }
    setDateSelection(event.target.value);
  };

  //  const autoSizeColumns = () => {
  //   if (props.isCompactTable) {
  //     window.innerWidth > 767 ? gridApi?.sizeColumnsToFit() : gridColumnApi?.autoSizeAllColumns();
  //   } else {
  //     window.innerWidth >= 1199 ? gridApi?.sizeColumnsToFit() : gridColumnApi?.autoSizeAllColumns();
  //   }
  // };

  function replacer(key: any, value: any) {
    const excludedFields = ['id', 'roleName'];
    if (excludedFields.includes(key) && gridColumnApi?.getColumn(key) === null) {
      return null;
    }
    return value;
  }

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      filter: props.hasColumnFilters,
    };
  }, []);

  const getQuickFilterText = (params: any) => {
    return JSON.stringify(params.data, replacer); //removes values but not keys
    // return JSON.stringify(Object.values(params.data), replacer); //removes keys but not values due to no match in replacer key check

    // slow method, removes values and keys
    // const step1 = JSON.stringify(params.data, replacer); //JSON string with unnecessary values removed
    // const step2 = JSON.parse(step1);
    // const step3 = JSON.stringify(Object.values(step2));//JSON string with all keys removed
    // return step3;
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    props.onGridReady?.(params.api);
    //autoSizeColumns();
  };

  const handleFirstDataRender = () => {
    gridPaginationChanged();
    setInitialFilters();
    // autoSizeColumns();
  };

  const onSelectionChanged = () => {
    const selectedRows = gridApi?.getSelectedRows();
    props.onSelectionChanged?.(selectedRows);
  };

  const handleGridSizeChange = (event: GridSizeChangedEvent) => {
    const avatarVisible = event.clientWidth > 575;
    gridColumnApi?.setColumnVisible('avatar', avatarVisible);
    // autoSizeColumns();
  };

  const changePageSize = (e: any) => {
    setPageSize(e.target.value);
  };

  const muiPaginationChanged = (e: any, page: number) => {
    gridApi?.paginationGoToPage(page - 1);
  };

  const gridPaginationChanged = (e?: PaginationChangedEvent) => {
    setCurrentPage((gridApi?.paginationGetCurrentPage() || 0) + 1);
    setPageCount(gridApi?.paginationGetTotalPages() || 1);
  };

  const setInitialFilters = () => {
    if (props.tableConfig) {
      gridApi?.setFilterModel(props.tableConfig.filterModel);
    }
  };

  const search = (e: any) => {
    gridApi?.setQuickFilter(e.target.value);
  };

  const getSelectAllText = (props: SimpleTableProps) => {
    const enabledField = props.headers?.find(cell => cell.cellRenderer?.name.includes('checkboxCellRenderer'))?.cellRendererParams.enabledField;
    const selectableCount = enabledField ? props.data.filter(data => data[enabledField]).length : props.data.length;
    if (selectableCount === props.data.length) {
      return t("simpleTable.selectAll")
    } else {
      return t("simpleTable.selectAllPossible", { maxCount: selectableCount })
    }
  };

  const handleSaveTable = (params: any) => {
    if (!props.tableConfig) return;
    const api = params.columnApi;
    const gridApi = params.api;
    const filterModel = gridApi.getFilterModel();
    const columnState = api.getColumnState();
    const values: ColumnConfig[] = columnState.map((column: any) => {
      const colConfig: ColumnConfig = {
        columnName: column.colId,
        isHidden: column.hide,
        sortDirection: column.sort
      }
      return colConfig
    });
    props.tableConfig.onSaveTableConfig(values, filterModel);
  };

  const getContextMenuItems = useCallback((params: any) => {
    const getContextMenuItems: any = ["copy", "export"];

    if (props.tableConfig) {
      getContextMenuItems.push({
        name: t("simpleTable.saveTableConfiguration"),
        action: () => handleSaveTable(params)
      });
      getContextMenuItems.push({
        name: t("simpleTable.resetTableConfiguration"),
        action: () => props.tableConfig?.onSaveTableConfig(null, null)
      })
    }
    return getContextMenuItems;
  }, []);

  const searchInTable = t("simpleTable.searchInTable");
  let headers = props.headers;
  if (props.tableConfig) {
    headers = headers.map(h => {
      const columnDefinition = props.tableConfig?.columns && props.tableConfig?.columns?.find(c => c.columnName === h.field);
      if (columnDefinition) {
        h.hide = columnDefinition.isHidden;
        h.sort = columnDefinition.sortDirection;
      }
      return h;
    });
  };

  return (
    props.isLoading ? <SimpleTableSkeleton /> :
      <div>
        <div className="ag-theme-alpine" data-testid="ag-theme-alpine-simple-table">
          <div className="d-flex flex-row flex-wrap" style={!props.disableSearch ? { marginBottom: '1rem' } : {}}>
            <div className='flex-grow-1 align-self-center'>
              {props.onSelectAll &&
                <div>
                  <CheckboxLabel
                    checked={props.allSelected || false}
                    disabled={false}
                    onChange={(e) => { props.onSelectAll?.(); }}
                    label={getSelectAllText(props)} />
                </div>}
              {props.tableFilters &&
                <TableFilters switches={props.tableFilters} />}
            </div>
            {props.dateFilter && <>
              <div className="pe-3 mb-2">
                <Select
                  label={t("simpleTable.date")}
                  displayEmpty
                  value={dateSelection}
                  sx={{ minWidth: 150 }}
                  onChange={handleDateSelection}>
                  {dateSelections.map(d => <MenuItem value={d.id} key={d.id}>{d.name}</MenuItem>)}
                </Select>
              </div>
              {dateSelection === DateSelectionEnum.Custom &&
                <div className="pe-3 mb-2">
                  <DateRangePicker
                    inputFormat="dd.MM.yyyy"
                    mask='__.__.____'
                    value={props.dateRange || [new Date(), new Date()]}
                    onChange={(newValue) => {
                      props.onSetDateRange && props.onSetDateRange(newValue);
                    }}
                    renderInput={(startProps: any, endProps: any) => (<>
                      <TextField ref={startProps.inputRef} {...startProps.inputProps}
                        label={t("simpleTable.from")}
                      />
                      <div><span className='mx-2'>-</span></div>
                      <TextField ref={endProps.inputRef} {...endProps.inputProps}
                        label={t("simpleTable.to")}
                      />
                    </>)}
                  />
                </div>
              }
            </>
            }
            {props.radioFilterOpts &&
              <div className='mb-3 mb-md-0'>
                <Typography
                  component='span'
                  variant='body4'
                  className='me-2 d-none d-md-inline-block'>
                  {t("simpleTable.show")}:
                </Typography>
                <ToggleButtonGroup
                  color="secondary"
                  exclusive
                  size="small"
                  value={props.radioFilterOpts.value}
                  onChange={(e, i) => {
                    if (i !== null) {
                      props.radioFilterOpts?.onChange?.(i);
                    }
                  }}
                  className='me-3'>
                  {props.radioFilterOpts.values.map(val =>
                    <ToggleButton key={val} value={val} className='px-3'>{capitalize(val)}</ToggleButton>
                  )}
                </ToggleButtonGroup>
              </div>
            }
            {!(props.disableSearch) &&
              <div>
                <TextField
                  label={props.dateFilter ? t("simpleTable.search") : undefined}
                  type="search"
                  onChange={search}
                  placeholder={props.dateFilter ? '' : t("simpleTable.searchInTable") || ""}
                  InputProps={{
                    startAdornment:
                      <InputAdornment position="start">
                        <Icon>search</Icon>
                      </InputAdornment>
                  }}
                />
              </div>
            }
          </div>
          <AgGridReact
            localeText={i18n.language === 'nb' ? AG_GRID_LOCALE_NBNO : AG_GRID_LOCALE_EN}
            rowData={props.data}
            onGridReady={onGridReady}
            rowSelection={props.singleCellSelection ? 'false' : 'single'}
            //enableCellTextSelection={true}
            rowHeight={lineHeight}
            domLayout={'autoHeight'}
            pagination={!!props.pageSize}
            paginationPageSize={pageSize}
            suppressPaginationPanel={true}
            onPaginationChanged={gridPaginationChanged}
            onSelectionChanged={onSelectionChanged}
            onGridSizeChanged={handleGridSizeChange}
            headerHeight={props.headerHeight}
            onFirstDataRendered={handleFirstDataRender}
            suppressColumnVirtualisation={true}
            getContextMenuItems={getContextMenuItems}
            defaultColDef={(props.resizable || props.hasColumnFilters) ? defaultColDef : undefined}
            rowClassRules={props.rowClassRules}
            getRowStyle={props.getRowStyle}
          >
            {headers.map(h => <AgGridColumn
              key={h.field}
              field={h.field}
              width={((viewWidth < breakPoint || !h.flex) ? h.width : undefined)}
              minWidth={h.minWidth}
              hide={h.hide}
              sort={h.sort}
              flex={((viewWidth >= breakPoint) ? h.flex : 0)}
              headerName={h.headerName}
              sortable={h.interactive ?? true}
              suppressMenu={!(h.interactive ?? true)}
              cellRenderer={h.cellRenderer}
              cellRendererParams={h.cellRendererParams}
              getQuickFilterText={getQuickFilterText}
              suppressSizeToFit={true}
              headerComponent={h.headerComponent}
              valueGetter={h.valueGetter}
              valueFormatter={h.valueFormatter}
              cellStyle={{
                'display': 'flex',
                'alignItems': 'center',
                ...h.cellStyle ?? {}
              }}
              headerClass={h.headerClass}
              menuTabs={["filterMenuTab", "columnsMenuTab", "generalMenuTab"]}
            />)
            }
          </AgGridReact>
          {!props.disablePagination && <div className='d-flex flex-md-row flex-wrap justify-content-end align-items-center'>
            <div className='mt-4 pe-4 d-flex align-items-center'>
              <Typography component='span' variant='body6' className='pe-1'>
                {t("simpleTable.showQuantity")}:
              </Typography>
              <Select
                value={pageSize}
                onChange={changePageSize}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </div>
            <div className='mt-4 pe-4'>
              <Typography component='span' variant='body6'>
                {t("simpleTable.total")}: {gridApi?.getDisplayedRowCount()}
              </Typography>
            </div>
            <div>
              {!!props.pageSize && props.data &&
                <Pagination
                  className="d-flex justify-content-end mt-4"
                  count={pageCount || 0}
                  page={currentPage || 0}
                  onChange={muiPaginationChanged}
                />
              }
            </div>
          </div>
          }
        </div>
      </div>
  );
}

export default SimpleTable;